import { useMemo } from 'react'
import { type BoundOperation, isGroupByOperation } from '../../OperationTypes.js'
import type { MetadataFields, Metadatafieldtypes } from '../../MetadataFields.js'
import { isExistingMetadataField } from '../../MetadataFields.js'
import type { GroupByOperationKey, ProjectionOperationKey } from '../../OperationRegistry.js'
import { OPERATION_REGISTRY } from '../../OperationRegistry.js'
import { MetadataFieldSelect } from './MetadataFieldSelect.js'
import type { VisualizationSampleContainer } from '../../VisualizationSampleContainer.js'

type GroupByWithOptionalProjection = {
  groupBy: BoundOperation<GroupByOperationKey, unknown>
  projection?: BoundOperation<ProjectionOperationKey, unknown>
}

export function GroupByEditorRow({
  value,
  onChange,
  onRemove,
  samples,
  metadataFields
}: {
  value: GroupByWithOptionalProjection
  onChange: (v: GroupByWithOptionalProjection) => void
  onRemove: () => void
  samples: VisualizationSampleContainer[]
  metadataFields: MetadataFields
}) {
  const fieldDescriptor = metadataFields[value.groupBy.metaDataField]

  const projectionOps = useMemo(() => {
    return Object.values(OPERATION_REGISTRY).filter((op) => op.type === 'projection' && op.applicableTo.includes(fieldDescriptor?.type))
  }, [fieldDescriptor])

  const handleFieldChange = (fieldKey: string) => {
    if (!isExistingMetadataField(fieldKey, metadataFields)) throw Error('unexpected field in field change')

    const groupOp = Object.values(OPERATION_REGISTRY)
      .filter((op) => isGroupByOperation(op))
      .find((op) => op.applicableTo.includes(metadataFields[fieldKey].type))
    if (!groupOp) return

    onChange({
      groupBy: {
        operationKey: groupOp.key,
        /* handwavy broken casting since custom fields are allowed */
        metaDataField: fieldKey as Metadatafieldtypes,
        config: groupOp.config?.defaultConfig ?? {}
      },
      projection: undefined
    })
  }

  const handleProjectionChange = (projKey: ProjectionOperationKey) => {
    const projOp = OPERATION_REGISTRY[projKey]
    if (projOp?.type !== 'projection') return

    onChange({
      ...value,
      projection: {
        operationKey: projOp.key,
        metaDataField: value.groupBy.metaDataField,
        config: projOp.config?.defaultConfig ?? {}
      }
    })
  }

  const handleRemoveProjection = () => {
    onChange({
      ...value,
      projection: undefined
    })
  }

  return (
    <div className="flex items-center gap-2 w-full">
      {/* Field selector */}
      <MetadataFieldSelect
        metadataFields={metadataFields}
        value={value.groupBy.metaDataField}
        onChange={handleFieldChange}
        samples={samples}
      />

      {/* Projection selector */}
      {projectionOps.length > 0 ? (
        <select
          className="select select-sm select-bordered"
          value={value.projection?.operationKey ?? ''}
          onChange={(e) =>
            e.target.value === '' ? handleRemoveProjection() : handleProjectionChange(e.target.value as ProjectionOperationKey)
          }
        >
          <option value="">Raw value</option>
          {projectionOps.map((op) => (
            <option key={op.key} value={op.key}>
              {op.label}
            </option>
          ))}
        </select>
      ) : (
        <span></span>
      )}

      <button className="btn btn-xs btn-outline btn-error" onClick={onRemove}>
        ✕
      </button>
    </div>
  )
}
